// Category CRUD: inline edit, sort arrows, guest-upload toggle, release switch,
// guarded deletes.
import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { toast } from '../components/toast.jsx';

function CategoryRow({ cat, onChanged, siblings }) {
	const [draft, setDraft] = useState(cat);
	const [busy, setBusy] = useState(false);
	const dirty =
		draft.name !== cat.name ||
		draft.description !== cat.description ||
		(draft.comingSoonText ?? '') !== (cat.comingSoonText ?? '') ||
		draft.allowGuestUpload !== cat.allowGuestUpload;

	const save = async () => {
		setBusy(true);
		try {
			await api.updateCategory(cat.id, {
				name: draft.name,
				description: draft.description,
				comingSoonText: draft.comingSoonText ?? '',
				allowGuestUpload: draft.allowGuestUpload,
			});
			toast('Gespeichert.', 'info');
			onChanged();
		} catch {
			toast('Speichern fehlgeschlagen.', 'error');
		} finally {
			setBusy(false);
		}
	};

	// Takes effect at once (no Speichern), so releasing asks first — guests see the
	// gallery the moment this returns.
	const toggleRelease = async () => {
		const release = !!cat.hidden;
		if (release && !window.confirm(`„${cat.name}“ jetzt für alle Gäste freigeben, die diese Galerie sehen dürfen?`)) return;
		setBusy(true);
		try {
			await api.updateCategory(cat.id, { hidden: !release });
			toast(release ? `„${cat.name}“ ist freigegeben.` : `„${cat.name}“ ist nur noch für dich sichtbar.`, 'info');
			onChanged();
		} catch {
			toast('Umschalten fehlgeschlagen.', 'error');
		} finally {
			setBusy(false);
		}
	};

	// Swap sort values with the neighbor — the list is always dense enough for that.
	const move = async (dir) => {
		const sorted = [...siblings].sort((a, b) => a.sort - b.sort);
		const at = sorted.findIndex((c) => c.id === cat.id);
		const other = sorted[at + dir];
		if (!other) return;
		setBusy(true);
		try {
			await api.updateCategory(cat.id, { sort: other.sort });
			await api.updateCategory(other.id, { sort: cat.sort });
			onChanged();
		} catch {
			toast('Sortierung fehlgeschlagen.', 'error');
		} finally {
			setBusy(false);
		}
	};

	const remove = async () => {
		if (!window.confirm(`Kategorie „${cat.name}“ löschen?`)) return;
		setBusy(true);
		try {
			await api.deleteCategory(cat.id, false);
			onChanged();
		} catch (err) {
			if (err.code === 'not_empty') {
				if (window.confirm(`„${cat.name}“ enthält ${cat.count} Medien. Wirklich ALLES endgültig löschen?`)) {
					try {
						await api.deleteCategory(cat.id, true);
						onChanged();
					} catch {
						toast('Löschen fehlgeschlagen.', 'error');
					}
				}
			} else {
				toast('Löschen fehlgeschlagen.', 'error');
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className='catrow'>
			<div className='catrow__sort'>
				<button type='button' className='catrow__sortbtn' onClick={() => move(-1)} disabled={busy} aria-label='Nach oben'>
					↑
				</button>
				<button type='button' className='catrow__sortbtn' onClick={() => move(1)} disabled={busy} aria-label='Nach unten'>
					↓
				</button>
			</div>
			<div className='catrow__fields'>
				<div className='catrow__line'>
					<input className='input catrow__name' value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} aria-label='Name' />
					<span className='catrow__slug'>/{cat.id}</span>
					<span className='catrow__count'>{cat.count} Medien</span>
				</div>
				<button
					type='button'
					role='switch'
					aria-checked={!cat.hidden}
					className={`release ${cat.hidden ? '' : 'release--on'}`}
					onClick={toggleRelease}
					disabled={busy}
				>
					<span className='release__track' aria-hidden='true'>
						<span className='release__thumb' />
					</span>
					{cat.hidden ? 'Nicht freigegeben – nur du siehst diese Galerie' : 'Für Gäste freigegeben'}
				</button>
				<input
					className='input'
					value={draft.description}
					placeholder='Beschreibung'
					onChange={(e) => setDraft({ ...draft, description: e.target.value })}
					aria-label='Beschreibung'
				/>
				<input
					className='input'
					value={draft.comingSoonText ?? ''}
					placeholder='„Bald verfügbar“-Text (für leere Galerie)'
					onChange={(e) => setDraft({ ...draft, comingSoonText: e.target.value })}
					aria-label='Bald-verfügbar-Text'
				/>
				<label className='catrow__toggle'>
					<input
						type='checkbox'
						checked={draft.allowGuestUpload}
						onChange={(e) => setDraft({ ...draft, allowGuestUpload: e.target.checked })}
					/>
					Gäste dürfen hier hochladen
				</label>
			</div>
			<div className='catrow__actions'>
				<button type='button' className='btn am__btn' onClick={save} disabled={!dirty || busy}>
					Speichern
				</button>
				<button type='button' className='btn btn--danger am__btn' onClick={remove} disabled={busy}>
					Löschen
				</button>
			</div>
		</div>
	);
}

export default function CategoryEditor() {
	const [categories, setCategories] = useState([]);
	const [newName, setNewName] = useState('');
	const [busy, setBusy] = useState(false);

	const load = () => {
		api.categories()
			.then(setCategories)
			.catch(() => toast('Kategorien konnten nicht geladen werden.', 'error'));
	};
	useEffect(load, []);

	const create = async (e) => {
		e.preventDefault();
		if (!newName.trim() || busy) return;
		setBusy(true);
		try {
			await api.createCategory({ name: newName.trim() });
			setNewName('');
			load();
		} catch (err) {
			toast(err.code === 'exists' ? 'Diese Kategorie gibt es schon.' : 'Anlegen fehlgeschlagen.', 'error');
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className='catedit'>
			{[...categories]
				.sort((a, b) => a.sort - b.sort)
				.map((cat) => (
					<CategoryRow key={cat.id} cat={cat} siblings={categories} onChanged={load} />
				))}
			<form className='catedit__new' onSubmit={create}>
				<input className='input' placeholder='Neue Kategorie …' value={newName} onChange={(e) => setNewName(e.target.value)} aria-label='Name der neuen Kategorie' />
				<button type='submit' className='btn btn--primary' disabled={busy || !newName.trim()}>
					Anlegen
				</button>
			</form>
		</section>
	);
}
